/* eslint-disable @typescript-eslint/naming-convention */
import { Button, Col, Form, Row, message, Alert } from 'src/antd';
import React, { useContext, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { PropertyContext } from 'src/context/property-context';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { T } from '@transifex/react';
import { useGetPropertyContainers } from 'src/api/property/property-containers';
import useMediaQuery from 'use-media-antd-query/es/useMediaQuery';
import { CssScrollerDiv, StyledSkeletonInput, Typography } from 'src/ui';
// eslint-disable-next-line import/no-cycle
import { useSmsServiceStore, TSubscription, TNotificationMode } from '../store';
import { getCookie } from '../../../../../utils/cookies';
import {
  emailCodeCookie,
  smsCodeCookie,
} from '../../../../../consts/cookie-consts';
// eslint-disable-next-line import/no-cycle
import { routingMap } from '../../../../../routing/routing-map';
import ContainerListItem from './ContainerListItem';
import { apiNotification } from '../../../../../api/notifications';
import { getSubscriptionStatus } from './subscription-utils';
import { getAlertConfig } from './alert-config';
import { confirmUnsubscribeAll } from './ConfirmUnsubscribeAll';

type TFormSchema = {
  containers: Record<string, { checked: boolean; daysBefore: number }>;
};

const NotificationSubscriptions = () => {
  const { id: propertyId } = useContext(PropertyContext);
  const [form] = Form.useForm<TFormSchema>();
  const history = useHistory();
  const { mode } = useSmsServiceStore.getState();
  const other = useMemo(() => useSmsServiceStore.getState().data?.other, []);

  const { data: dataContainers, isLoading } = useGetPropertyContainers(
    propertyId,
    {
      includePrices: true,
      includePreferredDates: true,
    }
  );

  const isNarrow = useMediaQuery('(max-width: 700px)');
  const subscriptions = useSmsServiceStore(
    (state) => state.data?.subscriptions
  );

  useEffect(() => {
    if (subscriptions && subscriptions.length > 0) {
      const newValue: TFormSchema['containers'] = Object.fromEntries(
        subscriptions.map(({ id, daysBefore }) => [
          id,
          { checked: true, daysBefore },
        ])
      );
      form.setFieldValue('containers', newValue);
    }
  }, [subscriptions, form]);

  const onCancel = () => {
    if (
      (mode === 'sms' && getCookie(smsCodeCookie)) ||
      mode === 'email' ||
      getCookie(emailCodeCookie)
    ) {
      history.push(routingMap.app.notifications._);
    } else {
      useSmsServiceStore.getState().reset();
    }
  };

  const handleAutoSave = async () => {
    if (
      (mode === 'sms' && !getCookie(smsCodeCookie)) ||
      (mode === 'email' && !getCookie(emailCodeCookie))
    ) {
      message.error(
        <T _str="Your session has expired, please verify again." />
      );

      useSmsServiceStore.getState().reset();

      return;
    }

    const values = form.getFieldsValue() as TFormSchema;
    const newPayload = Object.entries(values.containers || {})
      .filter(([, v]) => v?.checked)
      .map(([id, v]): TSubscription => ({ id, daysBefore: v.daysBefore }));

    // Update local state
    useSmsServiceStore.getState().updateSubscriptions(newPayload);

    // Send API request to server
    try {
      const { token } = useSmsServiceStore.getState().context;
      const { verificationCode } = useSmsServiceStore.getState().data;

      await apiNotification.mutateUpdateContainers({
        propertyId,
        token,
        verificationId: verificationCode,
        containers: newPayload.map(({ id, daysBefore }) => ({
          containerId: id,
          daysBefore,
        })),
      });
    } catch (e: $TSFixMe) {
      message.error(e?.message || <T _str="Error occured" />);
    }
  };

  useEffect(() => {
    if (mode === 'sms' && getCookie(smsCodeCookie)) {
      useSmsServiceStore
        .getState()
        .setVerificationCode(getCookie(smsCodeCookie), {
          ...other,
        });
    }
    if (mode === 'email' && getCookie(emailCodeCookie)) {
      useSmsServiceStore
        .getState()
        .setVerificationCode(getCookie(emailCodeCookie), {
          ...other,
        });
    }
  }, [mode, other]);

  const onSubscribeBulk = async (checked: boolean) => {
    if (!checked) {
      const confirmed = await confirmUnsubscribeAll();
      if (!confirmed) return;
    }

    const containersData = dataContainers?.data || [];

    form.setFields(
      containersData.map(({ container }: $TSFixMe) => ({
        name: ['containers', container.id],
        value: {
          checked,
          daysBefore: 1,
        },
      }))
    );

    handleAutoSave();
  };

  if (isLoading) {
    return (
      <Row gutter={[0, 8]}>
        {isLoading &&
          React.Children.toArray(
            Array.from({ length: 5 }).map(() => (
              <Col xs={24}>
                <StyledSkeletonInput
                  width="100%"
                  active
                  style={{ height: 80, borderRadius: 8 }}
                  size="large"
                />
              </Col>
            ))
          )}
      </Row>
    );
  }

  return (
    <Form
      scrollToFirstError
      form={form}
      onValuesChange={handleAutoSave}
      style={{ height: '100%' }}
    >
      <div
        className="wh-vertical-scroller-container"
        style={{
          rowGap: '16px',
          height: `calc(100vh - ${isNarrow ? 283 : 312}px)`,
        }}
      >
        {!isLoading && dataContainers?.data && (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const containers = getFieldValue('containers') || {};
              const { hasNoSubscriptions, allSubscribed } =
                getSubscriptionStatus(
                  containers,
                  dataContainers?.data?.length || 0,
                  subscriptions
                );

              const alertConfig = getAlertConfig(
                hasNoSubscriptions,
                allSubscribed,
                mode as TNotificationMode
              );

              return (
                <Alert showIcon {...alertConfig} style={{ marginBottom: 16 }} />
              );
            }}
          </Form.Item>
        )}
        <Row align="middle" gutter={[16, 16]} justify="space-between">
          <Col>
            <Typography weight="semi-bold" variant="h6">
              <T _str="Current containers" />
            </Typography>
          </Col>
          <Col>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const containers = getFieldValue('containers') || {};
                const { allSubscribed, evenOneSubscribed } =
                  getSubscriptionStatus(
                    containers,
                    dataContainers?.data?.length || 0,
                    subscriptions
                  );

                return (
                  <Row gutter={24}>
                    <Col>
                      <Button
                        disabled={isLoading || allSubscribed}
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => onSubscribeBulk(true)}
                      >
                        {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                        <>
                          <T _str="Subscribe all" />
                        </>
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        disabled={isLoading || !evenOneSubscribed}
                        type="primary"
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => onSubscribeBulk(false)}
                      >
                        {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                        <>
                          <T _str="Unsubscribe all" />
                        </>
                      </Button>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
          </Col>
        </Row>
        <div className="wh-vertical-scroller-container-scroll">
          <CssScrollerDiv defaultYScroller offset={16}>
            <Row gutter={[0, 8]}>
              {dataContainers?.data?.map(({ container }: $TSFixMe) => (
                <Col xs={24}>
                  <Form.Item noStyle name={['containers', container.id]}>
                    <ContainerListItem container={container} />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </CssScrollerDiv>
        </div>
        <Row gutter={[8, 8]} justify="end">
          <Col xs={isNarrow ? 24 : undefined}>
            <Button style={{ width: '100%' }} onClick={onCancel}>
              <T _str="Cancel" />
            </Button>
          </Col>
        </Row>
      </div>
    </Form>
  );
};

export default NotificationSubscriptions;
